import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-listings-create',
  templateUrl: './listings-create.component.html',
  styleUrls: ['./listings-create.component.scss']
})
export class ListingsCreateComponent implements OnInit {

  public Form: FormGroup;
  submitted = false;
  message = null;
  class = null;
  action = null;
  loading = false;
  countries = [];
  states = [];
  cities = [];
  loadingStates = false;
  loadingCities = false;

  constructor(
    private ActivatedRoute: ActivatedRoute, 
    private formBuilder: FormBuilder,  
    private http: HttpClient, 
    private router: Router, 
    @Inject('API_ENDPOINT') private api_endpoint: string
  ) {}

  ngOnInit() {
    this.action = 'Submit';
    
    // Initialize the form
    this.Form = this.formBuilder.group({
      business: ['', Validators.required],
      countryid: ['', Validators.required],
      stateid: ['', Validators.required],
      cityid: ['', Validators.required],
      address: ['', Validators.required],
      _id: [''],
    });

    // Load countries
    this.loadCountries();

    // Check if we're editing an existing listing
    this.ActivatedRoute.params.subscribe(params => {
      let id = params['id'];
      if (id) {
        this.loadListing(id);
      }
    });
  }

  // Get authentication headers with Bearer token
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });

    // Add Userinfo if still required by your API
    const userInfo = JSON.stringify({
      _id: localStorage.getItem('adminSess'), 
      role: localStorage.getItem('userRole')
    });
    
    if (userInfo) {
      headers = headers.set('Userinfo', userInfo);
    }

    return headers;
  }

  // Load countries
  loadCountries() {
    const headers = this.getAuthHeaders();
    
    this.http.get<any>(`${environment.apiUrl}/country/`, { headers }).subscribe(
      data => {
        if (data.message === 'Logged Out!') {
          this.router.navigateByUrl('/auth/login');
          return;
        }
        this.countries = data.results || data.data || [];
      },
      (err: HttpErrorResponse) => {
        this.handleError(err, 'Loading countries failed');
      }
    );
  }

  // Load listing data for editing
  loadListing(id: string) {
    this.loading = true;
    const headers = this.getAuthHeaders();
    
    this.http.get<any>(`${environment.apiUrl}/listingsById/${id}`, { headers }).subscribe(
      data => {
        this.loading = false;
        
        if (data.message === 'Logged Out!') {
          this.router.navigateByUrl('/auth/login');
          return;
        }
        
        if (data.data != null) {
          this.action = 'Update';
          this.Form.patchValue({
            _id: data.data._id,
            business: data.data.business,
            countryid: data.data.countryid,
            stateid: data.data.stateid,
            cityid: data.data.cityid,
            address: data.data.address,
          });

          // Load states for the selected country
          if (data.data.countryid) {
            this.getstates(data.data.countryid, true);
          }

          // Load cities for the selected country and state
          if (data.data.countryid && data.data.stateid) {
            this.getcities(data.data.countryid, data.data.stateid, true);
          }
        }
      },
      (err: HttpErrorResponse) => {
        this.loading = false;
        this.handleError(err, 'Loading listing failed');
      }
    );
  }

  // Get states by country
  getstates(value: string, silent: boolean = false) {
    console.log('Getting states for country:', value);
    
    if (!value) {
      this.states = [];
      this.cities = [];
      this.Form.patchValue({ stateid: '', cityid: '' });
      return;
    }

    if (!silent) {
      // Reset state and city when country changes
      this.Form.patchValue({ stateid: '', cityid: '' });
      this.cities = [];
    }

    this.loadingStates = true;
    const headers = this.getAuthHeaders();
    
    this.http.get<any>(`${environment.apiUrl}/state/states/bycountryId?country=${value}`, { headers }).subscribe(
      data => {
        this.loadingStates = false;
        console.log('States API response:', data);
        
        if (data.message === 'Logged Out!') {
          this.router.navigateByUrl('/auth/login');
          return;
        }
        this.states = data.results || data.data || [];
      },
      (err: HttpErrorResponse) => {
        this.loadingStates = false;
        console.error('Error loading states:', err);
        this.handleError(err, 'Loading states failed');
      }
    );
  }

  // Get cities by country and state - UPDATED TO USE BOTH PARAMETERS
  getcities(countryId: string, stateId: string, silent: boolean = false) {
    console.log('Getting cities for country:', countryId, 'and state:', stateId);
    
    if (!countryId || !stateId) {
      this.cities = [];
      this.Form.patchValue({ cityid: '' });
      return;
    }

    if (!silent) {
      // Reset city when state changes
      this.Form.patchValue({ cityid: '' });
    }

    this.loadingCities = true;
    const headers = this.getAuthHeaders();
    
    // Updated API call to use both country and state IDs
    const apiUrl = `${environment.apiUrl}/city/by-countryId-and-stateId/${countryId}/${stateId}`;
    console.log('Cities API URL:', apiUrl);
    
    this.http.get<any>(apiUrl, { headers }).subscribe(
      data => {
        this.loadingCities = false;
        console.log('Cities API response:', data);
        
        if (data.message === 'Logged Out!') {
          this.router.navigateByUrl('/auth/login');
          return;
        }
        
        // FIX: Handle different response formats
        if (Array.isArray(data)) {
          // Response is directly an array of cities
          this.cities = data;
        } else if (data.data && Array.isArray(data.data)) {
          // Response has data property containing array
          this.cities = data.data;
        } else if (data.results && Array.isArray(data.results)) {
          // Response has results property containing array
          this.cities = data.results;
        } else {
          // Default to empty array if format is unexpected
          this.cities = [];
          console.warn('Unexpected API response format for cities:', data);
        }
        
        console.log('Processed cities:', this.cities);
      },
      (err: HttpErrorResponse) => {
        this.loadingCities = false;
        console.error('Error loading cities:', err);
        this.handleError(err, 'Loading cities failed');
      }
    );
  }

  // Handler for when country selection changes
  onCountryChange() {
    const countryId = this.Form.value.countryid;
    console.log('Country changed to:', countryId);
    this.getstates(countryId);
  }

  // Handler for when state selection changes
  onStateChange() {
    const countryId = this.Form.value.countryid;
    const stateId = this.Form.value.stateid;
    console.log('State changed to:', stateId, 'for country:', countryId);
    this.getcities(countryId, stateId);
  }

  // Getter for easy access to form fields
  get f() { return this.Form.controls; }

  // Handle form submission
  onFormSubmit() {
    this.submitted = true;

    if (this.Form.invalid) {
      return;
    }

    this.loading = true;
    const headers = this.getAuthHeaders();

    if (this.action === 'Submit') {
      this.createListing(headers);
    } else {
      this.updateListing(headers);
    }
  }

  // Create a new listing
  createListing(headers: HttpHeaders) {
    const data = {
      "business": this.Form.value.business,
      "countryid": this.Form.value.countryid,
      "stateid": this.Form.value.stateid,
      "cityid": this.Form.value.cityid,
      "address": this.Form.value.address,
    };

    this.http.post<any>(`${environment.apiUrl}/listings/`, data, { headers }).subscribe(
      res => {
        this.loading = false;
        if (res.message === 'Logged Out!') {
          this.router.navigateByUrl('/auth/login');
          return;
        }
        
        if (res.status === '500') {
          this.showMessage(res.message, 'danger');
        } else {
          this.showMessage('Successfully Added', 'success');
          setTimeout(() => {
            this.router.navigateByUrl('/listings/list');
          }, 1500);
        }
      },
      (err: HttpErrorResponse) => {
        this.loading = false;
        this.handleError(err, 'Creating listing failed');
      }
    );
  }

  // Update an existing listing
  updateListing(headers: HttpHeaders) {
    const data = {
      "business": this.Form.value.business,
      "countryid": this.Form.value.countryid,
      "stateid": this.Form.value.stateid,
      "cityid": this.Form.value.cityid,
      "address": this.Form.value.address,
      "_id": this.Form.value._id,
    };

    this.http.put<any>(`${environment.apiUrl}/listings/`, data, { headers }).subscribe(
      res => {
        this.loading = false;
        if (res.message === 'Logged Out!') {
          this.router.navigateByUrl('/auth/login');
          return;
        }
        
        if (res.status === '500') {
          this.showMessage(res.message, 'danger');
        } else {
          this.showMessage('Successfully Updated', 'success');
          setTimeout(() => {
            this.router.navigateByUrl('/listings/list');
          }, 1500);
        }
      },
      (err: HttpErrorResponse) => {
        this.loading = false;
        this.handleError(err, 'Updating listing failed');
      }
    );
  }

  // Show message to user
  private showMessage(message: string, type: string) {
    this.message = message;
    this.class = type === 'success' ? 'alert alert-success' : 'alert alert-danger';
    window.scrollTo(0, 0);
    
    // Auto hide message after 5 seconds
    setTimeout(() => {
      this.message = null;
    }, 5000);
  }

  // Handle HTTP errors
  private handleError(error: HttpErrorResponse, context: string) {
    console.error(`${context}:`, error);
    
    if (error.error instanceof Error) {
      // Client-side error
      this.showMessage('An error occurred. Please check your connection and try again.', 'danger');
    } else {
      // Server-side error
      if (error.status === 400) {
        this.showMessage('Invalid request. Please check your input.', 'danger');
      } else if (error.status === 401 || error.status === 403) {
        this.showMessage('Authentication failed. Please login again.', 'danger');
        setTimeout(() => {
          this.router.navigateByUrl('/auth/login');
        }, 2000);
      } else if (error.status === 500) {
        this.showMessage('Server error. Please try again later.', 'danger');
      } else {
        this.showMessage('An unexpected error occurred. Please try again.', 'danger');
      }
    }
  }
}